import { DropdownIcon } from '@/assets/images';
import { Dropdown } from '@/components';
import EmojiButton from '@/components/EmojiButton';
import PropTypes from 'prop-types';
import React, { useRef } from 'react';
import './AppActionInput.scss';
import { updateToast } from '@/shared/utils';

const AppActionInputBase = (
   {
      label,
      id,
      value,
      onChange,
      placeholder,
      items = [],
      required = false,
      maxLength = null,
      showEmoji = false,
      showDropdown = false,
      prefixIcon = null,
      trailingComponent = null,
      containerClassName = '',
      className = '',
      showCharCount = true,
      ...rest
   },
   ref,
) => {
   const inputRef = useRef(null);
   const combinedRef = ref || inputRef;

   const handleInputChange = e => {
      const val = e.target.value;
      if (!maxLength || [...val].length <= maxLength) {
         onChange(val);
      } else {
         updateToast('input-max-length', `Maximum ${maxLength} characters allowed`, { type: 'warning' });
      }
   };

   const onEmojiClick = emoji => {
      const input = combinedRef.current;
      if (!input) return;

      const start = input.selectionStart;
      const end = input.selectionEnd;
      const text = value;
      const before = text.substring(0, start);
      const after = text.substring(end, text.length);
      const newValue = before + emoji + after;

      if (!maxLength || [...newValue].length <= maxLength) {
         onChange(newValue);
         // Move cursor after inserted emoji
         setTimeout(() => {
            input.selectionStart = input.selectionEnd = start + emoji.length;
            input.focus();
         }, 0);
      }
   };

   const displayDropdown = showDropdown && items.length > 0;

   return (
      <div className={`app-action-input-group ct-flex ct-fcol ${containerClassName}`}>
         {label && (
            <label htmlFor={id} className="app-action-label">
               {label}
               {required && <span className="required-mark">*</span>}
            </label>
         )}
         <div className="action-input-container ct-flex ct-aiCenter">
            {prefixIcon && <div className="action-input-prefix ct-flexCenter">{prefixIcon}</div>}
            <input
               ref={combinedRef}
               id={id}
               type="text"
               value={value}
               onChange={handleInputChange}
               placeholder={placeholder}
               required={required}
               className={`action-field ${className}`}
               {...rest}
            />

            {(showEmoji || displayDropdown || trailingComponent) && (
               <div className="action-buttons-wrapper ct-flex ct-aiCenter">
                  {showEmoji && <EmojiButton insertEmoji={onEmojiClick} className="action-emoji-trigger" />}

                  {showEmoji && displayDropdown && <div className="action-divider" />}

                  {displayDropdown && (
                     <div className="action-dropdown-wrapper">
                        <Dropdown
                           icon={
                              <i className="ct-flexCenter chevron-icon">
                                 <DropdownIcon />
                              </i>
                           }
                           items={items}
                           withPadding={false}
                           position="bottom-right"
                           buttonClassName="action-dropdown-trigger"
                           dropdownClassName="action-dropdown-menu"
                        />
                     </div>
                  )}

                  {trailingComponent}
               </div>
            )}
         </div>
         {showCharCount && maxLength && (
            <div className="action-char-count ct-flex ct-jcEnd">
               {Math.max(0, maxLength - [...value].length)}/{maxLength}
            </div>
         )}
      </div>
   );
};

const AppActionInput = React.forwardRef(AppActionInputBase);

AppActionInputBase.propTypes = {
   label: PropTypes.string,
   id: PropTypes.string,
   value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
   onChange: PropTypes.func.isRequired,
   placeholder: PropTypes.string,
   items: PropTypes.array,
   required: PropTypes.bool,
   maxLength: PropTypes.number,
   showEmoji: PropTypes.bool,
   showDropdown: PropTypes.bool,
   prefixIcon: PropTypes.node,
   trailingComponent: PropTypes.node,
   containerClassName: PropTypes.string,
   className: PropTypes.string,
   showCharCount: PropTypes.bool,
};

export default AppActionInput;
